/**
 * useParentSessionGesture — la machine des gestes partagés de l'espace parent.
 *
 * UNE machine pour les deux surfaces (l'accueil et « Tous mes lives »), parce que c'est le même
 * geste : décaler une séance, prévenir d'une absence, signaler un retard, écrire à un tuteur.
 * Elle a deux entrées, et c'est tout ce qui les distingue :
 *  - une action rapide de l'accueil, qui commence par « Quelle séance ? » (ou « Quel tuteur ? »,
 *    seulement s'il y en a plusieurs) ;
 *  - la feuille d'actions d'une séance, qui arrive avec la séance déjà choisie — l'étape se saute.
 *
 * Les gabarits du design system sont contrôlés : l'étape et les choix arrivent en props, les
 * gestes repartent en callbacks (`design-system/CLAUDE.md` § Contrôlé). Le réducteur ci-dessous
 * est pur et exporté : chaque transition se joue dans un test, sans monter d'écran.
 *
 * Appels, à l'identique du legacy :
 *  - créneaux et quota d'absences : `getRecoveryPossibilitiesAsParent` (`RecoverLiveSessionComponent`),
 *    lu aussi à la confirmation d'une absence à moins de 2 h, pour son encadré « Absences » ;
 *  - avec `asStudent` (l'élève sur son propre espace) : les appels de son ancien formulaire,
 *    `getRecoveryPossibilities` et `reportAbsenceAsStudent` (`POST /live-sessions/{id}/manage-attendance-flag`,
 *    gardée par la policy de la séance), sans `student_email` : le compte connecté est l'élève ;
 *  - drapeau : `reportAbsenceAsParent` → `POST /live-sessions/{id}/new-attendance-flag`
 *    (`AbsenceFormComponent.onSubmit`). Aucun endpoint, aucun champ nouveau.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Linking } from "react-native"

import { apiIdle, apiRequest } from "../../../../data/redux/actions/api"
import { liveService } from "../../../../data/services/liveService"
import { type LiveMenuAction, type LiveSessionAction } from "../../../../design-system"
import { type MoveSessionFlowProps, type MoveSessionStep } from "../../../../design-system/templates/MoveSessionFlow"
import { type ReportLateFlowProps, type ReportLateStep } from "../../../../design-system/templates/ReportLateFlow"
import { type TutorPickerModalProps } from "../../../../design-system/templates/TutorPickerModal"
import { type ConfirmDialogProps } from "../../../../design-system/molecules/ConfirmDialog"
import { type SnackbarProps } from "../../../../design-system/molecules/Snackbar"
import {
    absenceFlagBody,
    lateFlagBody,
    moveRecoveryReadout,
    recoveryPossibilitiesParams,
    recoveryRefusalFromError,
    recoveryRefusalSlug,
    NO_MOVE_RECOVERY,
    RECOVERY_SLOTS_FALLBACK_ERROR,
    type ApiAttendanceFlagBody,
    type ApiRecoveryPossibilities,
    type MoveRecoveryReadout,
} from "../../adapters/parent/Attendance/attendanceFlagAdapter"
import { childDisplayName, elide } from "../formatters/childFormatter"
import { canMoveSession, isGroupSession, isPaymentBlocked } from "../formatters/liveSessionFormatter"
import { sessionActions } from "../formatters/liveSessionActions"
import { type UILiveSession, type UIParentRecoverySlot, type UIParentTutorChoice, type UITutor } from "../types"
import { apiErrorMessage } from "./apiErrorMessage"
import { useGestureEmitter } from "./gestureTracking"
import { openExternalUrl } from "./openExternalUrl"
import { openSupportEmail as writeToSupport, SUPPORT_MAILTO_URL } from "./openSupportEmail"
import { useApiFetch } from "./useApiFetch"
import { selectDisplayedStudent } from "../../view-models/shared/displayedStudent"

/** Les familles d'essai sont suivies par l'équipe, pas par le tuteur — cibles du dialogue legacy. */
export const TRIAL_WHATSAPP_URL = "https://wa.me/message/FCOBQ6PCPMBJC1"
export { SUPPORT_MAILTO_URL }

const SUBMIT_FALLBACK_ERROR = "Impossible d'enregistrer. Veuillez réessayer."

/** Ce que l'accusé dit, par geste — c'est aussi ce que le bandeau reprend si la feuille est fermée. */
const NOTICE_MOVED = "Séance décalée"
const NOTICE_ABSENCE = "Absence notée"
const NOTICE_LATE = "Retard signalé"

export type ParentGestureFlow = "move" | "late" | "message" | "join" | "payment"

/**
 * Les étapes des deux gabarits, plus les trois qui n'appartiennent à aucun : « Quel tuteur ? »
 * (message), la confirmation de bascule (entrer en live à la place de l'enfant) et celle de
 * régularisation (la séance est suspendue).
 */
export type ParentGestureStep = MoveSessionStep | ReportLateStep | "tutor" | "join" | "payment"

export type ParentSessionGestureState = {
    flow: ParentGestureFlow | null
    step: ParentGestureStep
    session: UILiveSession | null
    slot: UIParentRecoverySlot | null
    minutes: number | null
    tutor: UIParentTutorChoice | null
    submitting: boolean
    error: string | null
}

export const initialParentSessionGestureState: ParentSessionGestureState = {
    flow: null,
    step: "session",
    session: null,
    slot: null,
    minutes: null,
    tutor: null,
    submitting: false,
    error: null,
}

export type ParentSessionGestureAction =
    | { type: "START_MOVE" }
    | { type: "START_LATE" }
    | { type: "START_MESSAGE"; tutorCount: number }
    | { type: "SESSION_ACTION"; session: UILiveSession; action: LiveSessionAction }
    | { type: "SESSION_BLOCKED"; session: UILiveSession }
    | { type: "SELECT_SESSION"; session: UILiveSession }
    | { type: "SELECT_SLOT"; slot: UIParentRecoverySlot }
    | { type: "SELECT_MINUTES"; minutes: number }
    | { type: "SELECT_TUTOR"; tutor: UIParentTutorChoice }
    | { type: "BACK_TO_TUTOR" }
    | { type: "CHOOSE_SLOT_LATER" }
    | { type: "DECLARE_ABSENCE" }
    | { type: "SWITCH_TO_LATE" }
    | { type: "TOO_LATE" }
    | { type: "SUBMIT" }
    | { type: "SUBMIT_SUCCESS" }
    | { type: "SUBMIT_ERROR"; message: string }
    | { type: "JOIN_CONFIRMED" }
    | { type: "PAYMENT_CONFIRMED" }
    | { type: "BACK" }
    | { type: "CLOSE" }

/** Les lignes de la feuille d'actions qui ouvrent chaque parcours (à moins de 2 h, décaler devient prévenir). */
const FLOW_SHEET_ACTIONS: Record<"move" | "late", LiveSessionAction[]> = {
    move: ["move_session", "report_absence"],
    late: ["report_late"],
}

/**
 * Les séances que « Quelle séance ? » propose : celles dont la feuille d'actions offre le geste
 * (`sessionActions`) — les deux entrées suivent une seule règle. Ni une séance suspendue pour
 * paiement ou terminée (la feuille n'offre rien), ni une absence notée ; un retard déjà signalé ne
 * se re-signale pas, mais la séance reste décalable. Aucune séance proposable : l'étape dit ce
 * qu'elle dit sans séance à venir — sauf si elles sont suspendues, où elle ne s'ouvre pas
 * (`paymentBlockedEntry`).
 */
export function pickableSessions(sessions: UILiveSession[], flow: "move" | "late"): UILiveSession[] {
    return sessions.filter((session) =>
        sessionActions(session).some((action) => FLOW_SHEET_ACTIONS[flow].includes(action))
    )
}

/**
 * La séance suspendue par laquelle une action rapide doit entrer, ou `null`. Quand l'enfant a des
 * séances mais qu'aucune ne se propose parce qu'elles sont suspendues pour paiement, « Quelle
 * séance ? » dirait « … n'a aucune séance à venir » sous une carte qui en montre une : l'action
 * pose la question d'un clic sur l'une d'elles, « Régulariser » — une séance suspendue ne mène
 * qu'à la régularisation (Wajih, 22/09). Sans aucune séance, la phrase est vraie et reste.
 */
export function paymentBlockedEntry(sessions: UILiveSession[], flow: "move" | "late"): UILiveSession | null {
    if (pickableSessions(sessions, flow).length > 0) return null
    return sessions.find(isPaymentBlocked) ?? null
}

/**
 * Le cours par lequel une action rapide doit entrer quand « Quelle séance ? » n'aurait rien à
 * proposer alors que l'enfant a un cours qui n'est pas en groupe — ou `null`. Un retard ne se
 * signale pas sur un cours individuel (`sessionActions`) : l'enfant qui n'a que ceux-là lirait
 * « … n'a aucune séance à venir » sous une carte qui en montre une. L'action ouvre donc ce que la
 * feuille de cette séance offre : « contactez le tuteur ».
 */
export function contactEntry(sessions: UILiveSession[], flow: "move" | "late"): UILiveSession | null {
    if (pickableSessions(sessions, flow).length > 0) return null
    return (
        sessions.find((session) => !isGroupSession(session) && sessionActions(session).includes("move_session")) ?? null
    )
}

/**
 * Par où le déplacement entre, une fois la séance connue. Trois branches, dans cet ordre :
 * un cours qui n'est pas en groupe se règle avec quelqu'un (le tuteur, ou l'équipe pour un
 * essai) ; à moins de 2 h, plus rien ne se déplace ; sinon on demande le nouveau créneau.
 */
export function moveEntryStep(session: UILiveSession): MoveSessionStep {
    if (!isGroupSession(session)) return "contactTutor"
    if (!canMoveSession(session)) return "tooLate"
    return "slot"
}

/**
 * Les étapes qui montrent la lecture de `recovery-possibilities` : les créneaux, et la
 * confirmation d'une absence à moins de 2 h, qui en lit le quota — comme le formulaire legacy,
 * qui montrait l'encadré « Absences » avant toute absence signalée. La lecture part à leur entrée
 * et se coupe à leur sortie.
 */
function readsRecovery(state: Pick<ParentSessionGestureState, "flow" | "step">): boolean {
    return state.flow === "move" && (state.step === "slot" || state.step === "absence")
}

export function parentSessionGestureReducer(
    state: ParentSessionGestureState,
    action: ParentSessionGestureAction
): ParentSessionGestureState {
    switch (action.type) {
        case "START_MOVE":
            return { ...initialParentSessionGestureState, flow: "move", step: "session" }

        case "START_LATE":
            return { ...initialParentSessionGestureState, flow: "late", step: "session" }

        case "START_MESSAGE":
            // Un seul tuteur : on ne pose pas une question qui n'a qu'une réponse — l'écran ouvre
            // directement le composeur et la machine ne bouge pas.
            return action.tutorCount > 1
                ? { ...initialParentSessionGestureState, flow: "message", step: "tutor" }
                : state

        case "SESSION_ACTION": {
            const { session } = action
            const opened = { ...initialParentSessionGestureState, session }
            if (action.action === "move_session") {
                return { ...opened, flow: "move", step: moveEntryStep(session) }
            }
            // Un drapeau ne se pose que sur un cours en groupe : la feuille n'offre pas ces lignes
            // ailleurs, et si l'une arrive quand même, elle mène là où « Décaler » mène.
            const flag = action.action === "report_absence" || action.action === "report_late"
            if (flag && !isGroupSession(session)) {
                return { ...opened, flow: "move", step: "contactTutor" }
            }
            if (action.action === "report_absence") {
                return { ...opened, flow: "move", step: "absence" }
            }
            if (action.action === "report_late") {
                return { ...opened, flow: "late", step: "minutes" }
            }
            if (action.action === "join_as_child") {
                return { ...opened, flow: "join", step: "join" }
            }
            // Le message et les annulations ne passent pas par la machine.
            return state
        }

        case "SESSION_BLOCKED":
            // La séance est suspendue : aucun geste n'aboutirait, on ne propose que le paiement.
            return { ...initialParentSessionGestureState, flow: "payment", step: "payment", session: action.session }

        case "SELECT_SESSION": {
            const { session } = action
            if (state.flow === "late") {
                if (!isGroupSession(session)) {
                    return { ...state, flow: "move", session, slot: null, step: "contactTutor", error: null }
                }
                return { ...state, session, step: "minutes", minutes: null, error: null }
            }
            if (state.flow === "move") {
                return { ...state, session, slot: null, step: moveEntryStep(session), error: null }
            }
            return state
        }

        case "SELECT_SLOT":
            return state.flow === "move" ? { ...state, slot: action.slot, error: null } : state

        case "SELECT_MINUTES":
            return state.flow === "late" ? { ...state, minutes: action.minutes, error: null } : state

        case "SELECT_TUTOR":
            // Le composeur prend la main : la feuille « Quel tuteur ? » se referme derrière lui. Le
            // choix reste, pour que le retour du composeur la rouvre dessus.
            return { ...state, flow: null, tutor: action.tutor }

        case "BACK_TO_TUTOR":
            return { ...initialParentSessionGestureState, flow: "message", step: "tutor", tutor: state.tutor }

        case "CHOOSE_SLOT_LATER":
            // « Je choisirai plus tard » — l'absence part sans rattrapage (`recovery_occurrence_id: null`).
            return { ...state, slot: null, submitting: true, error: null }

        case "DECLARE_ABSENCE":
            return { ...state, step: "absence", error: null }

        case "SWITCH_TO_LATE":
            return { ...state, flow: "late", step: "minutes", minutes: null, slot: null, error: null }

        case "TOO_LATE":
            // Le serveur a refusé le rattrapage pour délai : même écran que la garde client. Seule
            // l'étape des créneaux en dépend : relu pour le quota de la confirmation d'absence, ce
            // refus y est attendu, et la feuille ne bouge pas.
            if (state.flow !== "move" || state.step !== "slot") return state
            return { ...state, step: "tooLate", submitting: false, error: null }

        case "SUBMIT":
            return { ...state, submitting: true, error: null }

        case "SUBMIT_SUCCESS":
            return {
                ...state,
                submitting: false,
                error: null,
                step: state.flow === "move" && state.step === "absence" ? "absenceDone" : "done",
            }

        case "SUBMIT_ERROR":
            return { ...state, submitting: false, error: action.message }

        case "JOIN_CONFIRMED":
            // La bascule emporte l'espace parent : rien à rouvrir, rien à accuser. On efface
            // l'intention DANS le même temps qu'on la consomme — c'est ce qui la rend unique.
            return initialParentSessionGestureState

        case "PAYMENT_CONFIRMED":
            // Même garde que la bascule : la page de régularisation s'ouvre hors de l'app, et
            // l'intention s'efface en se consommant, donc elle ne peut pas partir deux fois.
            return initialParentSessionGestureState

        case "BACK":
            if (state.flow === "move") {
                // Depuis la confirmation d'absence, on revient à l'écran qui l'explique.
                if (state.step === "absence") return { ...state, step: "tooLate", error: null }
                return { ...state, step: "session", slot: null, error: null }
            }
            if (state.flow === "late") return { ...state, step: "session", minutes: null, error: null }
            return state

        case "CLOSE":
            return initialParentSessionGestureState

        default:
            return state
    }
}

/**
 * La séance que la confirmation de bascule doit emporter — `null` dès qu'elle est consommée.
 *
 * C'est la garde d'unicité du geste, et elle est lisible : la bascule de compte démonte l'arbre
 * de navigation, alors un second appel ne partirait pas sur un écran vivant. Annuler, fermer et
 * confirmer la ramènent tous à `null`.
 */
export function joinTarget(state: ParentSessionGestureState): UILiveSession | null {
    return state.flow === "join" ? state.session : null
}

/**
 * Le lien que « Régulariser » doit ouvrir — `null` dès qu'il n'y a rien à ouvrir : la question
 * n'est pas posée, le geste a déjà été consommé, ou le compte ne porte aucun lien de paiement.
 *
 * Même garde d'unicité que `joinTarget`, et même raison : la page s'ouvre hors de l'app, donc un
 * second appel partirait dans le vide. Le dernier cas est le plus utile à tenir ici : sans lien,
 * le bouton ne doit pas exister plutôt qu'ouvrir une page vide (`TRANSITION.md`, § Accueil).
 */
export function paymentTarget(state: ParentSessionGestureState, paymentUrl: string | null | undefined): string | null {
    return state.flow === "payment" && paymentUrl ? paymentUrl : null
}

type ApiSlice = {
    request?: boolean
    success?: boolean
    error?: unknown
    data?: unknown
    routeParams?: { id?: string | number }
}

/** Les props que `<ParentSessionGestures>` étale sur les cinq surfaces du design system. */
export type ParentSessionGesturesProps = {
    move: MoveSessionFlowProps
    late: ReportLateFlowProps
    tutorPicker: TutorPickerModalProps
    joinConfirm: ConfirmDialogProps
    paymentConfirm: ConfirmDialogProps
    notice: SnackbarProps
}

export type UseParentSessionGestureArgs = {
    /** Les prochaines séances de l'élève, triées — ce que l'étape « Quelle séance ? » propose. */
    sessions: UILiveSession[]
    /** Les tuteurs de l'élève — « Quel tuteur ? » ne s'ouvre qu'au-delà d'un seul. */
    tutors?: UIParentTutorChoice[]
    /** Prénom de l'enfant. À défaut celui du compte connecté : en mode parent, c'est lui. */
    childFirstname?: string
    studentEmail?: string | null
    /**
     * Ouvre le composeur de message sur ce tuteur — il est tenu par `useLiveSessionsListState`.
     * `onBack` n'est donné qu'après « Quel tuteur ? » : le composeur en est alors la 2ᵉ étape.
     */
    onOpenTutorMessage: (tutor: UITutor, onBack?: () => void) => void
    /** Les annulations gardent leur route : elles repartent vers `useLiveSessionsListState`. */
    onCancelFlag?: (session: UILiveSession, action: LiveMenuAction) => void
    /**
     * Quitte l'espace parent pour l'espace de l'enfant, et l'amène sur CETTE séance.
     *
     * ABSENT = pas de ligne « Rejoindre » dans la feuille d'actions : c'est ainsi que le pont
     * legacy dit « cet enfant n'a pas de jeton de bascule » ou « on est sous impersonation ».
     * La bascule elle-même est legacy (`ParentMainLayout.handleExitToChild`) et
     * `ports/**` n'importe pas `src/common/**` — elle arrive donc en prop, comme la navigation.
     */
    onJoinAsChild?: (session: UILiveSession) => void
    /**
     * Le lien de régularisation de la famille (`payment_url`) — le MÊME que celui de la ligne
     * « À faire », ouvert par la même porte (`openExternalUrl`). ABSENT = pas de bouton
     * « Régulariser » dans le dialogue : promettre une page qu'on ne sait pas ouvrir serait pire
     * que de ne rien promettre.
     */
    paymentUrl?: string | null
    /** Rejoué après un drapeau posé : la liste doit montrer le nouvel état. */
    refresh?: () => void
    /** L'écran qui porte la machine, en propriété `surface` de la mesure des gestes. */
    surface?: string
    /** Place à réserver sous le bandeau d'accusé — la hauteur de la barre d'onglets. */
    snackbarBottomOffset?: number
    /**
     * L'élève sur son propre espace, pas le parent : les appels de son ancien formulaire
     * d'absence (`AbsenceFormComponent`, branche `!asParent`). Ceux du parent mettent à jour
     * `formData.user`, que l'espace de l'élève n'a pas. Défaut : non. Lu au montage.
     */
    asStudent?: boolean
}

export type UseParentSessionGestureResult = {
    /** Action rapide « Décaler une séance ». */
    onMoveSession: () => void
    /** Action rapide « Signaler un retard ». */
    onReportLate: () => void
    /** Action rapide « Écrire à un tuteur » — absente quand aucun tuteur n'est connu. */
    onMessageTutor: (() => void) | undefined
    /** Le geste choisi dans la feuille d'actions d'une séance, la séance déjà attachée. */
    onSessionAction: (session: UILiveSession, action: LiveSessionAction) => void
    /**
     * Le clic sur une séance SUSPENDUE, avant toute feuille : la page le route ici au lieu
     * d'ouvrir ses actions, et la machine pose la question de la régularisation.
     */
    onBlockedSessionPress: (session: UILiveSession) => void
    /**
     * Ce que la feuille d'actions doit écrire dans « Rejoindre en tant que … ». `undefined`
     * quand la bascule n'est pas offerte : la feuille n'affiche alors pas la ligne.
     */
    joinAsChildFirstname: string | undefined
    gestures: ParentSessionGesturesProps
}

export function useParentSessionGesture(args: UseParentSessionGestureArgs): UseParentSessionGestureResult {
    const {
        sessions,
        tutors = [],
        studentEmail = null,
        onOpenTutorMessage,
        onCancelFlag,
        onJoinAsChild,
        paymentUrl = null,
        refresh,
        snackbarBottomOffset = 0,
        asStudent = false,
        surface,
    } = args
    const flagService = asStudent ? liveService.reportAbsenceAsStudent : liveService.reportAbsenceAsParent
    const slotsService = asStudent ? liveService.getRecoveryPossibilities : liveService.getRecoveryPossibilitiesAsParent

    const dispatch = useDispatch()
    const emitGesture = useGestureEmitter()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const student = useSelector((state: any) => selectDisplayedStudent(state))
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const flagReq = useSelector((state: any) => state?.api?.[flagService.name] as ApiSlice | undefined)

    const [state, dispatchUi] = useReducer(parentSessionGestureReducer, initialParentSessionGestureState)
    /**
     * La dernière lecture de `recovery-possibilities`, d'un bloc : les créneaux, le motif d'un
     * refus pour de bon (pas un échec technique — un « pas maintenant » n'en a pas), ce que
     * l'étape vide dit alors, et le quota d'absences.
     */
    const [recovery, setRecovery] = useState<MoveRecoveryReadout & { loading: boolean }>({
        ...NO_MOVE_RECOVERY,
        loading: false,
    })
    const [notice, setNotice] = useState<{ message: string; tone: "success" | "danger" } | null>(null)

    const awaitingFlagRef = useRef(false)
    const handledFlagErrorRef = useRef(false)
    /** Le drapeau en vol : sa séance, l'accusé de son succès, et le geste mesuré qu'il conclut. */
    const pendingFlagRef = useRef<{ id: string | number; notice: string; gesture: LiveSessionAction } | null>(null)

    const childFirstname = childDisplayName(args.childFirstname, student)
    /** `student_email` du corps : l'élève désigné, ou le compte connecté — en mode parent, l'enfant. */
    const flagStudentEmail = asStudent ? null : (studentEmail ?? (student?.email as string | undefined) ?? null)

    // ── Créneaux de rattrapage et quota d'absences ───────────────────────────────────────────

    const applyRecoveryPayload = useCallback((payload: ApiRecoveryPossibilities | null) => {
        setRecovery({ ...moveRecoveryReadout(payload), loading: false })
        if (recoveryRefusalSlug(payload) === "delay_too_short") dispatchUi({ type: "TOO_LATE" })
    }, [])

    // La tranche est partagée avec « Planifier un rattrapage » : une réponse n'est la nôtre que si
    // elle renvoie NOTRE séance et NOTRE date (`scopeUrlParams`).
    const { fetchWith: readRecovery, cancel: cancelRecovery } = useApiFetch<ApiRecoveryPossibilities>({
        service: slotsService,
        fetchOnMount: false,
        scopeUrlParams: ["live_session_id", "occurrence_date"],
        idleOnError: true,
        onSuccess: applyRecoveryPayload,
        onError: (error) => {
            const refusal = recoveryRefusalFromError(error)
            if (refusal != null) applyRecoveryPayload(refusal)
            else
                setRecovery({
                    ...NO_MOVE_RECOVERY,
                    refusal: apiErrorMessage(error, RECOVERY_SLOTS_FALLBACK_ERROR),
                    loading: false,
                })
        },
    })

    /**
     * Passe un geste à la machine et, quand l'étape où il mène montre la lecture (`readsRecovery`),
     * la lance dans le même geste : aucune image ne montre celle d'une autre séance. Un geste qui
     * peut mener aux créneaux ou à la confirmation d'absence passe par ici, pas par `dispatchUi` :
     * sinon l'étape s'ouvre sans rien lire.
     */
    const advance = useCallback(
        (action: ParentSessionGestureAction) => {
            dispatchUi(action)
            const next = parentSessionGestureReducer(state, action)
            if (next.session == null || !readsRecovery(next)) return
            setRecovery({ ...NO_MOVE_RECOVERY, loading: true })
            readRecovery(recoveryPossibilitiesParams({ session: next.session, studentEmail: flagStudentEmail }))
        },
        [state, readRecovery, flagStudentEmail]
    )

    // Hors de ces étapes, la lecture en vol est coupée : sa réponse ne traîne pas sur la tranche. La
    // dernière lecture reste, elle : l'accusé dit encore si le rattrapage était refusé.
    const recoveryOnScreen = readsRecovery(state)
    useEffect(() => {
        if (!recoveryOnScreen) cancelRecovery()
    }, [recoveryOnScreen, cancelRecovery])

    // ── Le drapeau d'absence / de retard ─────────────────────────────────────────────────────

    const submitFlag = useCallback(
        (session: UILiveSession, body: ApiAttendanceFlagBody, successNotice: string, gesture: LiveSessionAction) => {
            const liveSessionId = session.liveSessionId
            if (liveSessionId == null) {
                dispatchUi({ type: "SUBMIT_ERROR", message: SUBMIT_FALLBACK_ERROR })
                return
            }
            awaitingFlagRef.current = true
            handledFlagErrorRef.current = false
            pendingFlagRef.current = { id: liveSessionId, notice: successNotice, gesture }
            dispatch(apiRequest(flagService, { id: liveSessionId }, body) as never)
            emitGesture("submitted", { gesture, surface })
        },
        [dispatch, flagService, emitGesture, surface]
    )

    const isOurFlagResponse = useCallback((id: string | number | undefined) => {
        const pending = pendingFlagRef.current?.id
        return pending == null || id == null || String(id) === String(pending)
    }, [])

    useEffect(() => {
        if (!awaitingFlagRef.current || !flagReq?.success) return
        if (!isOurFlagResponse(flagReq.routeParams?.id)) return
        awaitingFlagRef.current = false
        handledFlagErrorRef.current = false
        const pending = pendingFlagRef.current
        pendingFlagRef.current = null
        if (pending != null) emitGesture("succeeded", { gesture: pending.gesture, surface })
        dispatch(apiIdle(flagService) as never)
        refresh?.()
        // L'étape « c'est fait » ne peut s'afficher que si la feuille est encore ouverte ; sinon
        // l'accusé passe par le bandeau, pour que le geste ne se termine jamais en silence.
        if (state.flow != null) dispatchUi({ type: "SUBMIT_SUCCESS" })
        else if (pending != null) setNotice({ message: pending.notice, tone: "success" })
    }, [dispatch, flagService, flagReq, isOurFlagResponse, refresh, state.flow, emitGesture, surface])

    useEffect(() => {
        if (!awaitingFlagRef.current || !flagReq?.error || handledFlagErrorRef.current) return
        if (!isOurFlagResponse(flagReq.routeParams?.id)) return
        handledFlagErrorRef.current = true
        awaitingFlagRef.current = false
        const pending = pendingFlagRef.current
        pendingFlagRef.current = null
        if (pending != null) emitGesture("failed", { gesture: pending.gesture, surface }, flagReq.error)
        const message = apiErrorMessage(flagReq.error, SUBMIT_FALLBACK_ERROR)
        // Feuille fermée = plus de prop où poser l'erreur : le bandeau la dit, en rouge.
        if (state.flow != null) dispatchUi({ type: "SUBMIT_ERROR", message })
        else setNotice({ message, tone: "danger" })
        dispatch(apiIdle(flagService) as never)
    }, [dispatch, flagService, flagReq, isOurFlagResponse, state.flow, emitGesture, surface])

    useEffect(
        () => () => {
            dispatch(apiIdle(flagService) as never)
        },
        [dispatch, flagService]
    )

    // ── Les gestes ───────────────────────────────────────────────────────────────────────────

    const startQuickAction = useCallback(
        (flow: "move" | "late") => {
            emitGesture("opened", { gesture: flow === "move" ? "move_session" : "report_late", surface })
            const blocked = paymentBlockedEntry(sessions, flow)
            if (blocked != null) {
                dispatchUi({ type: "SESSION_BLOCKED", session: blocked })
                return
            }
            const contact = contactEntry(sessions, flow)
            if (contact != null) dispatchUi({ type: "SESSION_ACTION", session: contact, action: "move_session" })
            else dispatchUi({ type: flow === "move" ? "START_MOVE" : "START_LATE" })
        },
        [sessions, emitGesture, surface]
    )
    const onMoveSession = useCallback(() => startQuickAction("move"), [startQuickAction])
    const onReportLate = useCallback(() => startQuickAction("late"), [startQuickAction])

    const openTutorMessage = useCallback(
        (tutor: UITutor) => {
            dispatchUi({ type: "CLOSE" })
            onOpenTutorMessage(tutor)
        },
        [onOpenTutorMessage]
    )

    const backToTutor = useCallback(() => dispatchUi({ type: "BACK_TO_TUTOR" }), [])

    const onMessageTutor = useCallback(() => {
        const only = tutors.length === 1 ? tutors[0] : undefined
        if (only != null) {
            onOpenTutorMessage(only.tutor)
            return
        }
        dispatchUi({ type: "START_MESSAGE", tutorCount: tutors.length })
    }, [tutors, onOpenTutorMessage])

    const onSessionAction = useCallback(
        (session: UILiveSession, action: LiveSessionAction) => {
            if (action === "message_tutor") {
                openTutorMessage(session.tutor)
                return
            }
            if (action === "cancel_absence" || action === "cancel_late" || action === "cancel_recovery") {
                onCancelFlag?.(session, action)
                return
            }
            // Seuls les gestes qui finissent par une écriture s'ouvrent : rejoindre n'en a pas.
            if (action !== "join_as_child") emitGesture("opened", { gesture: action, surface })
            advance({ type: "SESSION_ACTION", session, action })
        },
        [openTutorMessage, onCancelFlag, advance, emitGesture, surface]
    )

    const selectSession = useCallback(
        (session: UILiveSession) => advance({ type: "SELECT_SESSION", session }),
        [advance]
    )

    const declareAbsence = useCallback(() => advance({ type: "DECLARE_ABSENCE" }), [advance])

    const submitMove = useCallback(() => {
        const { session, slot } = state
        if (session == null || slot == null) return
        dispatchUi({ type: "SUBMIT" })
        submitFlag(
            session,
            absenceFlagBody({ session, recoveryOccurrenceId: slot.id, studentEmail: flagStudentEmail }),
            NOTICE_MOVED,
            "move_session"
        )
    }, [state, submitFlag, flagStudentEmail])

    const chooseSlotLater = useCallback(() => {
        const { session } = state
        if (session == null) return
        dispatchUi({ type: "CHOOSE_SLOT_LATER" })
        submitFlag(
            session,
            absenceFlagBody({ session, recoveryOccurrenceId: null, studentEmail: flagStudentEmail }),
            NOTICE_ABSENCE,
            "move_session"
        )
    }, [state, submitFlag, flagStudentEmail])

    const submitAbsence = useCallback(() => {
        const { session } = state
        if (session == null) return
        dispatchUi({ type: "SUBMIT" })
        submitFlag(
            session,
            absenceFlagBody({ session, recoveryOccurrenceId: null, studentEmail: flagStudentEmail }),
            NOTICE_ABSENCE,
            "report_absence"
        )
    }, [state, submitFlag, flagStudentEmail])

    const submitLate = useCallback(() => {
        const { session, minutes } = state
        if (session == null || minutes == null) return
        dispatchUi({ type: "SUBMIT" })
        submitFlag(
            session,
            lateFlagBody({ session, minutes, studentEmail: flagStudentEmail }),
            NOTICE_LATE,
            "report_late"
        )
    }, [state, submitFlag, flagStudentEmail])

    const close = useCallback(() => dispatchUi({ type: "CLOSE" }), [])
    const dismissNotice = useCallback(() => setNotice(null), [])

    const confirmJoin = useCallback(() => {
        const session = joinTarget(state)
        if (session == null) return
        dispatchUi({ type: "JOIN_CONFIRMED" })
        onJoinAsChild?.(session)
    }, [state, onJoinAsChild])

    const onBlockedSessionPress = useCallback(
        (session: UILiveSession) => dispatchUi({ type: "SESSION_BLOCKED", session }),
        []
    )

    const confirmPayment = useCallback(() => {
        const url = paymentTarget(state, paymentUrl)
        if (url == null) return
        dispatchUi({ type: "PAYMENT_CONFIRMED" })
        openExternalUrl(url)
    }, [state, paymentUrl])

    const openTrialWhatsApp = useCallback(() => {
        dispatchUi({ type: "CLOSE" })
        void Linking.openURL(TRIAL_WHATSAPP_URL)
    }, [])

    const openSupportEmail = useCallback(() => {
        dispatchUi({ type: "CLOSE" })
        // L'élève écrit au support du geste de son espace : sur le web, sans onglet laissé vide.
        if (asStudent) writeToSupport()
        else void Linking.openURL(SUPPORT_MAILTO_URL)
    }, [asStudent])

    const gestures = useMemo<ParentSessionGesturesProps>(() => {
        // `flow` décide quelle feuille est visible : l'étape ne peut donc appartenir qu'à elle.
        const moveStep = (state.flow === "move" ? state.step : "session") as MoveSessionStep
        const lateStep = (state.flow === "late" ? state.step : "session") as ReportLateStep
        const moveRefusal = state.step === "slot" ? recovery.refusal : null
        const hasPaymentLink = !!paymentUrl

        return {
            move: {
                visible: state.flow === "move",
                onClose: close,
                step: moveStep,
                childFirstname,
                sessions: pickableSessions(sessions, "move"),
                selectedSession: state.session,
                onSelectSession: selectSession,
                slots: recovery.slots,
                slotsLoading: recovery.loading,
                selectedSlot: state.slot,
                onSelectSlot: (slot) => dispatchUi({ type: "SELECT_SLOT", slot }),
                onChooseSlotLater: chooseSlotLater,
                slotsEmptyMessage: recovery.emptyMessage,
                absenceQuota: recovery.quota,
                onSubmit: submitMove,
                submitLoading: state.submitting,
                onBack: () => dispatchUi({ type: "BACK" }),
                onReportLate: () => dispatchUi({ type: "SWITCH_TO_LATE" }),
                onMessageTutor: () => {
                    if (state.session != null) openTutorMessage(state.session.tutor)
                },
                onDeclareAbsence: declareAbsence,
                onSubmitAbsence: submitAbsence,
                absenceLoading: state.submitting,
                contactKind: state.session?.roomType === "trial" ? "trial" : "individual",
                onContactWhatsApp: openTrialWhatsApp,
                onContactEmail: openSupportEmail,
                chosenLater: state.slot == null,
                errorMessage: state.error ?? moveRefusal ?? undefined,
                errorSeverity: state.error != null ? "alert" : "warning",
            },
            late: {
                visible: state.flow === "late",
                onClose: close,
                step: lateStep,
                childFirstname,
                sessions: pickableSessions(sessions, "late"),
                selectedSession: state.session,
                onSelectSession: selectSession,
                minutes: state.minutes,
                onSelectMinutes: (minutes) => dispatchUi({ type: "SELECT_MINUTES", minutes }),
                onSubmit: submitLate,
                submitLoading: state.submitting,
                onBack: () => dispatchUi({ type: "BACK" }),
                errorMessage: state.error ?? undefined,
            },
            tutorPicker: {
                visible: state.flow === "message",
                onClose: close,
                childFirstname,
                tutors,
                selectedId: state.tutor?.tutor.id,
                onSelect: (choice) => {
                    dispatchUi({ type: "SELECT_TUTOR", tutor: choice })
                    onOpenTutorMessage(choice.tutor, backToTutor)
                },
            },
            joinConfirm: {
                visible: state.flow === "join",
                title: `Passer sur l'espace ${elide(childFirstname)}\u00a0?`,
                message: `Vous quittez l'espace parent pour rejoindre le live en tant ${elide(childFirstname, "que")}.`,
                confirmLabel: "Rejoindre",
                cancelLabel: "Annuler",
                // On ne détruit rien : la question n'est pas destructrice, le bouton reste marine.
                danger: false,
                onConfirm: confirmJoin,
                onCancel: close,
            },
            paymentConfirm: {
                visible: state.flow === "payment",
                title: "Paiement en attente",
                message:
                    "Les séances sont suspendues tant qu'il n'est pas réglé. Régularisez pour pouvoir rejoindre le live.",
                // Sans lien, la boîte ANNONCE au lieu de demander : le parent doit savoir
                // pourquoi la séance ne s'ouvre pas, mais on ne lui promet pas une page qu'on ne
                // sait pas ouvrir. La ligne « À faire » se comporte déjà ainsi.
                confirmLabel: hasPaymentLink ? "Régulariser" : "Fermer",
                cancelLabel: "Annuler",
                hideCancel: !hasPaymentLink,
                // Rien ne se détruit : le bouton reste marine.
                danger: false,
                onConfirm: hasPaymentLink ? confirmPayment : close,
                onCancel: close,
            },
            notice: {
                visible: notice != null,
                message: notice?.message ?? "",
                tone: notice?.tone ?? "success",
                onDismiss: dismissNotice,
                bottomOffset: snackbarBottomOffset,
            },
        }
    }, [
        state,
        sessions,
        tutors,
        childFirstname,
        recovery,
        notice,
        close,
        selectSession,
        declareAbsence,
        chooseSlotLater,
        submitMove,
        submitAbsence,
        submitLate,
        openTutorMessage,
        onOpenTutorMessage,
        backToTutor,
        openTrialWhatsApp,
        openSupportEmail,
        confirmJoin,
        confirmPayment,
        paymentUrl,
        dismissNotice,
        snackbarBottomOffset,
    ])

    return {
        onMoveSession,
        onReportLate,
        // Aucun tuteur connu : pas de destinataire, donc pas d'action — la tuile ne réagit plus.
        onMessageTutor: tutors.length > 0 ? onMessageTutor : undefined,
        onSessionAction,
        onBlockedSessionPress,
        // Pas de bascule possible = pas de prénom = pas de ligne dans la feuille.
        joinAsChildFirstname: onJoinAsChild != null ? childFirstname : undefined,
        gestures,
    }
}
